<template>
    <div class="features">

        <div class="global text-center">

            <div class="mb-3">
                <base-button type="secondary" class="nav-button btn-icon-only rounded-circle mr-0"
                    :class="{ show: showNavButtons }"
                    @click="filtersModal.show = true"
                >
                    <span class="btn-inner--icon"><i class="fas fa-tasks"></i></span>
                </base-button>
                <form-modal
                    v-model="filtersModal"
                    size="lg"
                    backdrop="true"
                    :title="$t('common.filters')"
                    :centered="false"
                >
                    <filters :value="filters" @input="filtersChanged"
                        :types="types"
                        :lang="lang"
                        :registration="registration"
                    >
                    </filters>
                </form-modal>
            </div>

            <div class="mb-4">
                <base-button type="secondary"
                    class="btn-icon-only rounded-circle mr-0"
                    @click="infoModal.show = true"
                >
                    <span class="btn-inner--icon"><i class="far fa-clipboard"></i></span>
                </base-button>
                <form-modal
                    v-model="infoModal"
                    size="xl"
                    backdrop="true"
                    :title="registration.degree.name + ' / ' + simulation.name"
                    :centered="false"
                >
                    <log-info :log="log"></log-info>
                    <div class="text-center">
                        <base-button type="primary" class="ml-auto" @click="infoModal.show = false">
                            {{ $t('common.close') }}
                        </base-button>
                    </div>
                </form-modal>
            </div>

            <div class="mb-3" v-if="active && !simulation.remedial">
                <base-button type="primary"
                        class="btn-icon-only rounded-circle mr-0"
                        @click="createIssue">
                    <span class="btn-inner--icon"><i class="fas fa-exclamation"></i></span>
                    <span class="badge badge-md badge-circle badge-floating badge-warning no-border"
                            v-if="stats && stats.global.issues">
                        {{ stats.global.issues }}
                    </span>
                </base-button>
            </div>

            <div class="mb-3" v-if="active">
                <base-button type="primary"
                        class="btn-icon-only rounded-circle mr-0"
                        @click="createPicture">
                    <span class="btn-inner--icon"><i class="fas fa-image"></i></span>
                    <span class="badge badge-md badge-circle badge-floating badge-warning no-border"
                            v-if="stats && stats.global.pictures">
                        {{ stats.global.pictures }}
                    </span>
                </base-button>
            </div>

            <div class="mb-3" v-if="active">
                <base-button type="primary"
                        class="btn-icon-only rounded-circle mr-0"
                        @click="createNote">
                    <span class="btn-inner--icon"><i class="fas fa-edit"></i></span>
                    <span class="badge badge-md badge-circle badge-floating badge-warning no-border"
                            v-if="stats && stats.global.notes">
                        {{ stats.global.notes }}
                    </span>
                </base-button>
            </div>
        </div>

        <portal to="modals">

            <form-modal v-model="modals.editNote" size="lg"
                :title="$t('feedbacks.notes')"
                :loading="noteForm.loading"
                :scroll-reset="false"
                :animationDuration="0"
                :centered="false"
            >
                <new-existing-tabs>
                    <template v-slot:new>
                        <note-form v-model="noteForm.data"
                            :errors="noteForm.errors"
                            :modal="modals.editNote"
                            :registration="registration"
                            :session="session"
                            :competencies="competenciesSelect"
                        >
                        </note-form>
                    </template>
                    <template v-slot:existing>
                        <feedbacks ref="notes"
                            type="note"
                            :modal="modals.editNote"
                            :registration="registration"
                            :simulation="simulation"
                            :session="session"
                            :competencies="competencies"
                            @update="updateNote"
                            @remove="removeNote"
                        >
                        </feedbacks>
                    </template>
                </new-existing-tabs>
            </form-modal>

            <form-modal v-model="modals.editPictureFile" size="lg"
                :title="$t('feedbacks.pictures')"
                :loading="pictureForm.loading"
                :scroll-reset="false"
                :animationDuration="0"
                :centered="false"
            >
                <new-existing-tabs>
                    <template v-slot:new>
                        <picture-file-form v-model="pictureForm.data"
                            :errors="pictureForm.errors"
                            :modal="modals.editPictureFile"
                            :registration="registration"
                            :session="session"
                            :competencies="competenciesSelect"
                            :endpoint="feedbackEndpoint"
                            @change="refresh"
                        >
                        </picture-file-form>
                    </template>
                    <template v-slot:existing>
                        <feedbacks ref="pictures"
                            type="picture"
                            :modal="modals.editPictureFile"
                            :registration="registration"
                            :simulation="simulation"
                            :session="session"
                            :competencies="competencies"
                            @update="updatePicture"
                            @remove="removePicture"
                        >
                        </feedbacks>
                    </template>
                </new-existing-tabs>
            </form-modal>

            <form-modal v-model="modals.editIssue" size="lg"
                :title="$t('common.issues')"
                :loading="issueForm.loading"
                :scroll-reset="false"
                :animationDuration="0"
                :centered="false"
            >
                <new-existing-tabs>
                    <template v-slot:new>
                        <issue-form v-model="issueForm.data"
                            :errors="issueForm.errors"
                            :modal="modals.editIssue"
                        >
                        </issue-form>
                    </template>
                    <template v-slot:existing>
                        <issues ref="issues"
                            :modal="modals.editIssue"
                            :registration="registration"
                            :simulation="simulation"
                            :session="session"
                            @update="updateIssue"
                            @remove="removeIssue"
                        >
                        </issues>
                    </template>
                </new-existing-tabs>
            </form-modal>

            <form-modal v-model="modals.updateNote"
                :title="$t('feedbacks.note')"
                :loading="noteFormForUpdate.loading"
                size="lg"
                :centered="false"
            >
                <note-form v-model="noteFormForUpdate.data"
                    :errors="noteFormForUpdate.errors"
                    :modal="modals.updateNote"
                    :registration="registration"
                    :session="session"
                    :competencies="competenciesSelect"
                >
                </note-form>
            </form-modal>

            <form-modal v-model="modals.updatePicture"
                :title="$t('feedbacks.picture')"
                :loading="pictureFormForUpdate.loading"
                size="lg"
                :centered="false"
            >
                <picture-form v-model="pictureFormForUpdate.data"
                    :errors="pictureFormForUpdate.errors"
                    :modal="modals.updatePicture"
                    :registration="registration"
                    :session="session"
                    :competencies="competenciesSelect"
                >
                </picture-form>
            </form-modal>

            <form-modal v-model="modals.updateIssue"
                :title="$t('common.issue')"
                :loading="issueFormForUpdate.loading"
                size="lg"
                :centered="false"
            >
                <issue-form v-model="issueFormForUpdate.data"
                    :errors="issueFormForUpdate.errors"
                    :modal="modals.updateIssue"
                >
                </issue-form>
            </form-modal>

            <confirm-modal v-model="modals.confirmDeleteIssue">
                <p>{{ $t('issues.delete-confirm') }}</p>
            </confirm-modal>

            <confirm-modal v-model="modals.confirmDeleteFeedback">
                <p>{{ $t('feedbacks.delete-confirm') }}</p>
            </confirm-modal>

        </portal>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import CrudForm from '@trax/classes/CrudForm'
    import CrudClient from '@trax/classes/CrudClient'
    import NewExistingTabs from './NewExistingTabs'
    import NoteForm from '../common/NoteForm'
    import PictureForm from '../common/PictureForm'
    import PictureFileForm from '../common/PictureFileForm'
    import IssueForm from '../common/IssueForm'
    import Feedbacks from './Feedbacks'
    import Issues from './Issues'
    import Filters from './Filters'
    import LogInfo from './LogInfo'
    import moment from 'moment'

    export default {

        components: {
            NewExistingTabs, NoteForm, PictureForm, PictureFileForm, IssueForm, Feedbacks, Issues, Filters, LogInfo
        },

        props: {

            log: {
                type: Object,
                required: true
            },

            registration: {
                type: Object,
                required: true
            },

            simulation: {
                type: Object,
                required: true
            },

            session: {
                type: Object,
                default: null
            },

            stats: {
                type: Object,
                default: null
            },

            competencies: {
                type: Object,
                default: null
            },
            
            gridCompetencies: {
                type: Object,
                default: null
            },

            lang: {
                type: String,
                default: "en"
            },
            
            types: {
                type: Object,
                default: {}
            },

            filters: {
                type: Object,
                default: {}
            },
        },

        data() {
            const feedbackEndpoint = this.session
                ? '/astral/api/front/dashboard/sessions/' + this.session.id + '/feedbacks'
                : '/astral/api/front/dashboard/registrations/' + this.registration.id + '/simulations/' + this.simulation.id + '/feedbacks'
            
            const issueEndpoint = this.session
                ? '/astral/api/front/dashboard/sessions/' + this.session.id + '/issues'
                : '/astral/api/front/dashboard/registrations/' + this.registration.id + '/simulations/' + this.simulation.id + '/issues'

            let feedbackClient = new CrudClient(feedbackEndpoint, {
                registration_id: this.registration.id,
                simulation_id: this.simulation.id,
                session_id: this.session ? this.session.id : null,
            })
            let issueClient = new CrudClient(issueEndpoint, {
                registration_id: this.registration.id,
                simulation_id: this.simulation.id,
                session_id: this.session ? this.session.id : null,
            })
            return {
                feedbackEndpoint,
                issueEndpoint,
                feedbackClient,
                issueClient,
                noteForm: new CrudForm(feedbackClient, this.mapNote, this.unmapNote),
                pictureForm: new CrudForm(feedbackClient, this.mapPicture, this.unmapPicture),
                issueForm: new CrudForm(issueClient, this.mapIssue, this.unmapIssue),
                noteFormForUpdate: new CrudForm(feedbackClient, this.mapNote, this.unmapNote),
                pictureFormForUpdate: new CrudForm(feedbackClient, this.mapPicture, this.unmapPicture),
                issueFormForUpdate: new CrudForm(issueClient, this.mapIssue, this.unmapIssue),
                modals: new ModalsController([
                    'editNote', 'updateNote',
                    'editPictureFile', 'updatePicture',
                    'confirmDeleteFeedback',
                    'editIssue', 'updateIssue', 'confirmDeleteIssue'
                ]),
                showNavButtons: false,
                filtersModal: { show: false },
                infoModal: { show: false },
                moment: moment,
            }
        },

        created() {
            this.noteForm.init(this, 'noteForm')
            this.noteFormForUpdate.init(this, 'noteFormForUpdate')
            this.pictureForm.init(this, 'pictureForm')
            this.pictureFormForUpdate.init(this, 'pictureFormForUpdate')
            this.issueForm.init(this, 'issueForm')
            this.issueFormForUpdate.init(this, 'issueFormForUpdate')
            this.modals.init(this, 'modals')
        },

        mounted() {
            $(window).scroll(() => {
                this.showNavButtons = $(window).scrollTop() > 300
            });
        },

        computed: {

            active() {
                return this.$permissions.hasRight('dashboard.pilot-log.access')
                    || (this.session && this.session.started_at && !this.session.closed_at)
            },

            competenciesSelect() {
                let select = {}
                if (!this.gridCompetencies) {
                    return select
                }
                for (let id in this.gridCompetencies) {
                    select[id] = this.gridCompetencies[id]['name_'+this.lang]
                }
                return select
            },
        },

        methods: {
            
            refresh() {
                this.$emit('refresh')
            },

            filtersChanged($event) {
                this.filtersModal.show = false
                this.$emit('filtersChanged', $event)
            },

            createNote() {
                this.modals.open('editNote')
                this.noteForm.create().then(resp => {
                    this.modals.close('editNote')
                    this.refresh()
                })
            },

            createPicture() {
                this.modals.open('editPictureFile')
                this.pictureForm.create().then(resp => {
                    this.modals.close('editPictureFile')
                    this.refresh()
                })
            },

            createIssue() {
                this.modals.open('editIssue')
                this.issueForm.create().then(resp => {
                    this.modals.close('editIssue')
                    this.refresh()
                })
            },

            updateNote(feedback) {
                this.modals.open('updateNote')
                this.noteFormForUpdate.update(feedback).then(resp => {
                    this.modals.close('updateNote')
                    this.resetModal('editNote')
                    this.$refs.notes.list()
                })
            },

            updatePicture(feedback) {
                this.modals.open('updatePicture')
                this.pictureFormForUpdate.update(feedback).then(resp => {
                    this.modals.close('updatePicture')
                    this.resetModal('editPictureFile')
                    this.$refs.pictures.list()
                })
            },

            updateIssue(issue) {
                this.modals.open('updateIssue')
                this.issueFormForUpdate.update(issue).then(resp => {
                    this.modals.close('updateIssue')
                    this.resetModal('editIssue')
                    this.$refs.issues.list()
                })
            },

            removeNote(feedback) {
                this.modals.open('confirmDeleteFeedback').then(resp => {
                    if (resp.confirmed) {
                        this.resetModal('editNote')
                        this.feedbackClient.delete(feedback.id).then(resp => {
                            this.$refs.notes.list()
                            this.refresh()
                        })
                    }
                })
            },

            removePicture(feedback) {
                this.modals.open('confirmDeleteFeedback').then(resp => {
                    if (resp.confirmed) {
                        this.resetModal('editPictureFile')
                        this.feedbackClient.delete(feedback.id).then(resp => {
                            this.$refs.pictures.list()
                            this.refresh()
                        })
                    }
                })
            },

            removeIssue(issue) {
                this.modals.open('confirmDeleteIssue').then(resp => {
                    if (resp.confirmed) {
                        this.resetModal('editIssue')
                        this.issueClient.delete(issue.id).then(resp => {
                            this.$refs.issues.list()
                            this.refresh()
                        })
                    }
                })
            },

            resetModal(name) {
                this.modals.close(name)
                this.$nextTick(() => {
                    this.modals.open(name)
                });
            },

            mapPicture(feedback) {
                let data = this.mapFeedback(feedback)
                // We need to add this now because data will be directly sent by the form,
                // without passing by the unmapFeedback method.
                data.type = 'picture'
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.timeslot_id = null
                data.learner1_id = this.session ? this.session.learner1_id : null
                data.learner2_id = this.session ? this.session.learner2_id : null
                return data
            },

            mapNote(feedback) {
                return this.mapFeedback(feedback)
            },

            mapFeedback(feedback) {
                return {
                    id: feedback ? feedback.id : null,
                    log_time_from: feedback ? feedback.log_time_from : null,
                    log_time_to: feedback ? feedback.log_time_to : null,
                    learner_summary: feedback ? feedback.learner_summary : null,
                    public_description: feedback ? feedback.public_description : null,
                    confidential_description: feedback ? feedback.confidential_description : null,
                    competency_ids: feedback ? (feedback.competency_ids
                        // Competency_ids may already be defined when coming from PWA (not from the back-end).
                        ? feedback.competency_ids
                        : feedback.competencies.map(competency => competency.id.toString())
                    ) : null,
                    personal: feedback ? feedback.personal == 1 || feedback.personal == "true" : true,
                    learner1_check: feedback ? feedback.learner1_id != null : false,
                    learner2_check: feedback ? feedback.learner2_id != null : false,
                }
            },

            mapIssue(issue) {
                return {
                    id: issue ? issue.id : null,
                    log_time_from: issue ? issue.log_time_from : null,
                    log_time_to: issue ? issue.log_time_to : null,
                    entered_at: issue ? issue.entered_at : null,
                    summary: issue ? issue.summary : null,
                    description: issue ? issue.description : null,
                }
            },

            unmapNote(data) {
                return this.unmapFeedback(data, 'note')
            },

            unmapPicture(data) {
                return this.unmapFeedback(data, 'picture')
            },

            unmapFeedback(data, type) {
                data.type = type
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.timeslot_id = null
                data.learner1_id = data.learner1_check && this.session ? this.session.learner1_id : null
                data.learner2_id = data.learner2_check && this.session ? this.session.learner2_id : null
                return data
            },

            unmapIssue(data) {
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.entered_at = this.moment().format('YYYY-MM-DD HH:mm:ss')
                data.timeslot_id = null
                data.author_role = this.$permissions.hasRight('dashboard.pilot-log.access')
                    ? 'pilot'
                    : 'instructor'
                return data
            },
        }
    }
</script>

<style scoped>

    .features .global {
        position: fixed;
        top: 200px;
        z-index: 1000;
    }
    .features .global >>> button .badge {
        border: none;
        width: 1.4rem;
        height: 1.4rem;
    }
    .no-border {
        border-width: 0
    }
    .nav-button {
        opacity: 0;
        visibility: hidden;
        transition:
            opacity .5s,
            visibility .5s;
    }
    .nav-button.show {
        opacity: 1;
        visibility: visible;
    }

</style>
